import React, { useMemo, useLayoutEffect, useRef, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';

function useContainerWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

export default function UsageSeriesNivo({
  title = 'Usage',
  subtitle,
  data,
  valueSuffix = 'h', // se ignora en yMode="number" (queda por compatibilidad)
  granularity = 'days',
  height = 320,
  yMax, // si querés forzar un máximo (en la misma unidad de y)
  yExactMax = false, // (no usado aquí; lo dejo por compatibilidad)
  yFormatter, // opcional: sobreescribe el formateo del eje Y y tooltip
  tooltipFormatter, // opcional: sobreescribe solo el tooltip
  shadeLast = false, // por defecto deshabilitado (no se dibuja el rectángulo)
  color = '#2A57C7',
  yMode = 'time', // 'time' (segundos) | 'number' (conteos / valores crudos)
  yTickCount = 6, // cantidad de ticks en Y (default 6)
}) {
  const [wrapRef, width] = useContainerWidth();

  // Rotación responsive
  const tickRotation = width < 640 ? -45 : width < 1024 ? -30 : 0;

  // Despeje para que las labels queden debajo de la línea inferior
  const labelOffsetY = width < 640 ? 18 : width < 1024 ? 14 : 10;
  const bottomMargin = tickRotation ? (width < 640 ? 96 : 84) : 56;

  // Serie (no convertimos unidades: asumimos que y ya viene en segundos o es numérico crudo)
  const series = useMemo(
    () => [
      {
        id: 'usage',
        data: (data ?? []).map((d) => ({
          x: d.label,
          y: d.value == null || d.value === '' ? 0 : Number(d.value),
        })),
      },
    ],
    [data],
  );

  // -------- TICKS: TIEMPO (segundos) --------
  function getNiceTicksSeconds(maxSec, count = 6) {
    const SLOTS = Math.max(1, count - 1);
    const max = Math.max(0, Math.ceil(Number(maxSec) || 0));

    let unit = 'sec';
    if (max >= 3600) unit = 'hour';
    else if (max >= 60) unit = 'min';

    const STEPS = {
      sec: [1, 2, 5, 10, 15, 30],
      min: [1, 2, 5, 10, 15, 20, 30],
      hour: [0.25, 0.5, 1, 1.5, 2, 3, 4, 6, 12, 24],
    };
    const toSec = (u, v) => (u === 'sec' ? v : u === 'min' ? v * 60 : v * 3600);

    let step = STEPS[unit].find((s) => toSec(unit, s) * SLOTS >= max);
    if (!step) {
      const raw = Math.ceil(max / SLOTS);
      step = unit === 'sec' ? raw : unit === 'min' ? Math.ceil(raw / 60) : Math.ceil(raw / 3600);
    }

    const niceMax = toSec(unit, step) * SLOTS;
    const ticks = Array.from({ length: count }, (_, i) => i * toSec(unit, step));

    const format = (sec) => {
      const s = Math.round(sec);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const ss = s % 60;
      if (unit === 'hour') return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      if (h > 0)
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(ss).padStart(
          2,
          '0',
        )}`;
      return `${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    };

    return { ticks, niceMax, format };
  }

  // -------- TICKS: NÚMEROS (genéricos) --------
  function getNiceTicksNumber(maxVal, count = 6) {
    const SLOTS = Math.max(1, count - 1);
    const max = Math.max(0, Number(maxVal) || 0);

    if (max === 0) {
      const ticks = Array.from({ length: count }, (_, i) => i);
      const niceMax = count - 1;
      const format = (v) =>
        new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(v);
      return { ticks, niceMax, format };
    }

    const rough = max / SLOTS;
    const pow10 = Math.pow(10, Math.floor(Math.log10(rough)));
    const frac = rough / pow10;
    let niceFrac;
    if (frac <= 1) niceFrac = 1;
    else if (frac <= 2) niceFrac = 2;
    else if (frac <= 5) niceFrac = 5;
    else niceFrac = 10;

    const step = niceFrac * pow10;
    const niceMax = step * SLOTS;
    const ticks = Array.from({ length: count }, (_, i) => i * step);

    const nf = new Intl.NumberFormat();
    const format = (v) => nf.format(v);

    return { ticks, niceMax, format };
  }

  // Máximo de datos (en la unidad actual que llegue en y)
  const yMaxRaw = useMemo(
    () => Math.max(0, ...series[0].data.map((p) => Number(p.y) || 0)),
    [series],
  );

  // Si te pasan yMax (override), úsalo; si no, usa el real
  const effectiveMax = Math.max(0, Number(yMax ?? yMaxRaw) || 0);

  // Elegimos el set de ticks y formateador según yMode
  const {
    ticks: yTicks,
    niceMax: yNiceMaxAuto,
    format: autoYFormat,
  } = useMemo(() => {
    return yMode === 'number'
      ? getNiceTicksNumber(effectiveMax, yTickCount)
      : getNiceTicksSeconds(effectiveMax, yTickCount);
  }, [yMode, effectiveMax, yTickCount]);

  // Si pasás yFormatter, tiene prioridad
  const formatY = useMemo(() => {
    if (yMode === 'number') return autoYFormat;
    // En tiempo: si el rango es < 60s, siempre mostrar mm:ss
    if (effectiveMax < 60) return autoYFormat;
    return typeof yFormatter === 'function' ? yFormatter : autoYFormat;
  }, [yMode, yFormatter, autoYFormat, effectiveMax]);

  // Helpers para labels X (opcional; los mantengo por compatibilidad con tus layouts)
  const splitWeekLabel = (label) => {
    const s = String(label ?? '')
      .trim()
      .replace(/^\(|\)$/g, '')
      .replace(/\s+/g, ' ');
    const m = s.match(/^(WEEK\s*\d+)[\s–—-]*(.*)$/i);
    if (m) return { top: m[1].trim(), bottom: m[2].replace(/^\(|\)$/g, '').trim() };
    return { top: s, bottom: '' };
  };

  const splitMonthLabel = (label) => {
    const s = String(label ?? '')
      .trim()
      .replace(/\s+/g, ' ');
    const m = s.match(/^(.*\S)\s+(\d{4})$/);
    if (m) return { top: m[1], bottom: m[2] };
    return { top: s, bottom: '' };
  };

  // Sombreado del último bucket (queda deshabilitado por default)
  const LastBucketShade = useMemo(
    () => (props) => {
      if (!shadeLast || series[0].data.length === 0) return null;
      const { xScale, innerHeight } = props;
      const pts = series[0].data;
      const last = pts[pts.length - 1];
      const xLast = xScale(last.x);
      let bucketW = typeof xScale.bandwidth === 'function' ? xScale.bandwidth() : 0;
      if (!bucketW) {
        const prev = pts[pts.length - 2];
        bucketW = prev ? Math.max(8, Math.abs(xLast - xScale(prev.x))) : 24;
      }
      return (
        <g>
          <defs>
            <linearGradient id="lastPeriod" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.12" />
              <stop offset="100%" stopColor={color} stopOpacity="0.06" />
            </linearGradient>
          </defs>
          <rect
            x={xLast - bucketW / 2}
            y={0}
            width={bucketW}
            height={innerHeight}
            fill="url(#lastPeriod)"
            rx={8}
          />
        </g>
      );
    },
    [series, shadeLast, color],
  );

  const xValues = useMemo(() => (series?.[0]?.data || []).map((p) => String(p.x)), [series]);
  const xLabelsSig = useMemo(() => xValues.join('|'), [xValues]);
  const chartKey = useMemo(
    () => `usage-line-${granularity}-${xLabelsSig}`,
    [granularity, xLabelsSig],
  );

  const looksLikeWeek = (s) => /^WEEK\s*\d+/i.test(String(s));
  const looksLikeMonth = (s) => /\b\d{4}\s*$/.test(String(s));
  const xKind = useMemo(() => {
    if (!xValues.length) return null;
    const sample = xValues[Math.floor(xValues.length / 2)];
    if (looksLikeWeek(sample)) return 'week';
    if (looksLikeMonth(sample)) return 'month';
    return 'other';
  }, [xValues]);

  const xAxisMismatch =
    (granularity === 'week' && xKind !== 'week') || (granularity === 'month' && xKind !== 'month');

  const renderBottomTick = useMemo(
    () => (tick) => {
      if (xAxisMismatch) return null;

      const splitBottomLabel = (label) => {
        if (granularity === 'month') return splitMonthLabel(label);
        if (granularity === 'week') return splitWeekLabel(label);
        return { top: String(label ?? ''), bottom: '' };
      };
      const { x, y, textX, textY, opacity, value } = tick;
      const { top, bottom } = splitBottomLabel(value);
      return (
        <g transform={`translate(${x},${y})`} opacity={opacity}>
          <text
            transform={`translate(${textX},${textY + labelOffsetY}) rotate(${tickRotation})`}
            textAnchor="middle"
            dominantBaseline="hanging"
            style={{ fill: '#64748b', fontSize: 12, pointerEvents: 'none' }}
          >
            <tspan x="0" dy="0">
              {top}
            </tspan>
            {bottom && (
              <tspan x="0" dy="1.2em" style={{ fill: '#94a3b8' }}>
                {bottom}
              </tspan>
            )}
          </text>
        </g>
      );
    },
    [tickRotation, labelOffsetY, xAxisMismatch, granularity],
  );

  return (
    <div ref={wrapRef} className="w-full rounded-2xl border border-slate-200 bg-white p-4 sm:p-6">
      {title && <h3 className="text-xl font-semibold text-slate-900">{title}</h3>}
      {subtitle && <p className="mb-4 text-sm text-slate-500">{subtitle}</p>}

      <div style={{ height }}>
        <ResponsiveLine
          key={chartKey}
          enableSlices="x"
          animate={false}
          sliceTooltip={({ slice }) => {
            const p = slice.points[0];
            const yVal = Number(p.data.y) || 0;
            return (
              <div className="w-[150px] rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm shadow">
                <div className="font-medium text-slate-900">{p.data.xFormatted}</div>
                <div className="text-slate-600">
                  {yMode === 'number'
                    ? autoYFormat(yVal)
                    : typeof tooltipFormatter === 'function'
                    ? tooltipFormatter(yVal)
                    : formatY(yVal)}{' '}
                </div>
              </div>
            );
          }}
          data={series}
          margin={{ top: 10, right: 16, bottom: bottomMargin, left: 44 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: yNiceMaxAuto, // tope “nice” según yMode
            stacked: false,
            nice: false,
          }}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisLeft={{
            tickSize: 0,
            tickPadding: 8,
            tickValues: yTicks,
            format: (v) => formatY(v),
          }}
          gridYValues={yTicks}
          axisBottom={{
            tickSize: 0,
            tickPadding: 8,
            renderTick: renderBottomTick,
          }}
          enableGridX={false}
          colors={color}
          lineWidth={3}
          pointSize={6}
          pointBorderWidth={2}
          pointColor="#ffffff"
          pointBorderColor={color}
          enableArea={false}
          theme={{
            grid: { line: { stroke: '#e5e7eb', strokeWidth: 1 } },
            axis: { ticks: { text: { fill: '#64748b', fontSize: 12 } } },
            tooltip: { container: { borderRadius: 12, border: '1px solid #e5e7eb' } },
          }}
          layers={[
            'grid',
            LastBucketShade,
            'markers',
            'areas',
            'lines',
            'points',
            'slices',
            'axes',
            'legends',
            'annotations',
          ]}
        />
      </div>
    </div>
  );
}
